import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const MarketsTable = ({ markets, onSort, onRowClick }) => {
  const { t } = useTranslation(["markets"]);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: t("Name"),
        sortable: true,
        filter: true,
        // Names typically need more space
        flex: 1,
      },
      {
        field: "venueName",
        headerName: t("Venue"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "productName",
        headerName: t("Product"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "termScheduleName",
        headerName: t("TermSchedule"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        flex: 1,
        headerClass: "ag-center-aligned-header",
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  // Handle sort changes to maintain consistent sorting API
  const handleSortChanged = (event) => {
    if (!onSort) return;

    const sortModel = event.api.getSortModel();
    if (sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      onSort(colId, sort);
    } else {
      onSort(null, null);
    }
  };

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Enable pagination for all tables regardless of size
      enabled: true,
      // Default page size
      pageSize: 20,
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  return (
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={markets}
      onSortChanged={onSort ? handleSortChanged : undefined}
      onRowClicked={(params) => onRowClick && onRowClick(params.data)}
      getRowId={(params) => params.data.marketId.toString()}
      animateRows={true}
      // Always use normal layout
      domLayout="autoHeight"
      // Enable pagination
      pagination={true}
      // Configure pagination settings
      paginationPageSize={paginationConfig.pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      // Enable panel to show entries info
      suppressPaginationPanel={false}
      // Set height for the grid
    />
  );
};

export default MarketsTable;

